'use client';

import { useCallback, useMemo } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { hasInAppHistory } from '@/lib/nav-history';
import { DockPods } from './dock';
import { CaretLeft, DotsThree } from './icons';
import { PathTrail } from './path-trail';
import { Skeleton } from './ui';
import { PageTitle } from './ui/page-title';
import { useDismissTapMount, useTapTarget, type TapTarget } from './tap-mount-store';
import { loadingChromeFor, type LoadingChrome, type ShellNames } from './loading-chrome';
import { useT } from '@/lib/locale-context';
import trailStyles from './path-trail.module.css';
import itemStyles from '@/app/items/[itemId]/drilldown.module.css';
import styles from './loading-chrome.module.css';

/**
 * The shell's loading chrome (docs/LOADING.md §4.2): the top bar's title or
 * trail and the dock while a surface loads, for every mount: the tap's, the
 * route's skeleton, and a direct visit or a refresh. `loading-chrome.ts`
 * decides; this file draws.
 *
 * Why the shell and not the skeletons: on a direct visit React leaves a
 * streamed Suspense fallback dehydrated while its boundary is pending, so a
 * skeleton's `TopbarSlot` portal or dock claim never runs there. The shell is
 * outside every pending boundary: its server render is in the first flush and
 * it hydrates at once.
 *
 * Read through the tap's store and the shell's own props, never a context
 * whose value changes (ui-kit.md, 2026-09-27).
 */
export function useLoadingChrome(navClaimed: boolean, shell: ShellNames): LoadingChrome {
  const tapTarget = useTapTarget();
  const pathname = usePathname();
  return useMemo(
    () => loadingChromeFor({ pathname, tapTarget, navClaimed, shell }),
    [pathname, tapTarget, navClaimed, shell],
  );
}

/**
 * The top bar's default seat, beside the base slot: the route's known title,
 * the trail of an item surface, or a shape in the geometry of either. The
 * shell's CSS hides it once a page has portaled its own header into the base
 * slot (`.topbarSlot:not(:empty)`), and shows it over everything while the
 * tap's mount stands for a page (`data-loading`).
 */
export function LoadingTitle({ chrome, className }: { chrome: LoadingChrome; className: string }) {
  const t = useT();
  const { title } = chrome;
  if (title.kind === 'none') return null;
  return (
    <div className={className} data-testid="loading-chrome-title">
      {title.kind === 'text' ? (
        // The page's own header bar is a flex row seating its title at its
        // start; this one does the same.
        <div className={styles.bar}>
          <PageTitle as="div" testId="loading-title">
            {t.msg(title.text)}
          </PageTitle>
        </div>
      ) : title.kind === 'shape' ? (
        // A line in the title's face, so it takes the title's height.
        <div className={styles.bar} aria-hidden>
          <PageTitle as="div">
            <Skeleton width="120px" />
          </PageTitle>
        </div>
      ) : (
        // The item page's own header bar class, so the trail lands where the
        // page's `PathTrail` will.
        <div className={itemStyles.headerBar}>
          {title.kind === 'trail' ? (
            <PathTrail root={title.trail.root} items={title.trail.items} />
          ) : (
            <div className={trailStyles.trail} aria-hidden data-testid="skeleton-trail">
              <span className={styles.trailBack} />
              <span className={trailStyles.crumb}>
                <Skeleton width="72px" />
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** The width of the split act's shape in the item's loading dock: the pill
 *  `ItemDock` draws for a new item (the tick, its box and "Done", and its
 *  caret, 2026-11-03), so the "…" beside it does not move when the page
 *  lands. A longer word widens the real pill to the left; its right edge, top
 *  and height are the shape's. */
const DOCK_PILL_WIDTH = '122px';

/** Back while a surface loads (docs/LOADING.md §4.2), the rule `ItemDock`
 *  and `SectionDock` follow: while the tap's mount stands, the dismissal
 *  (history holds no entry for the target yet); once the route has committed,
 *  pop the navigation that opened it, or walk to the trail's last crumb when
 *  popping would leave the app. */
function useLoadingBack(target: TapTarget | null): () => void {
  const router = useRouter();
  const dismissTapMount = useDismissTapMount();
  const trail = target?.trail;
  const fallback = trail?.items.at(-1)?.href ?? trail?.root?.href ?? '/work';
  return useCallback(() => {
    if (dismissTapMount()) return;
    if (hasInAppHistory()) router.back();
    // Replaced, never pushed, so two pages never bounce (src/lib/nav-history.ts).
    else router.replace(fallback);
  }, [dismissTapMount, router, fallback]);
}

/**
 * The dock's row while an item surface loads, drawn by the shell in place of
 * the nav lanes (the shell also draws the action slot empty, as `ItemDock`
 * and `SectionDock` claim it):
 *
 * - an item: `ItemDock`'s row, `[ ‹ ]` and `[ … | pill ]`, with a working
 *   Back, the "…" present but disabled (its rows need the item), and the
 *   pill's shape in the word's seat;
 * - a section: `SectionDock`, the back-only dock.
 */
export function LoadingDock({
  kind,
  target,
}: {
  kind: 'item' | 'section';
  target: TapTarget | null;
}) {
  const t = useT();
  const back = useLoadingBack(target);
  // Held by identity. The row was a slot claim until slice 4, and a claim
  // re-registers whenever its children change while the shell re-renders on
  // every registration; a fresh row per render registered without end and
  // starved the navigation's transition. The shell draws it directly now,
  // but it re-renders on every tap and every claim, and the row stays one
  // element across them.
  return useMemo(
    () => (
      <DockPods
        left={[
          {
            key: 'back',
            label: t('shell.loading.back'),
            icon: <CaretLeft />,
            onClick: back,
            testId: 'dock-back',
          },
        ]}
        right={
          kind === 'item'
            ? [
                {
                  key: 'meta',
                  label: t('shell.loading.aboutItem'),
                  icon: <DotsThree aria-hidden />,
                  disabled: true,
                  testId: 'dock-item-meta-loading',
                },
                {
                  key: 'next',
                  label: t('shell.loading.nextStep'),
                  shape: DOCK_PILL_WIDTH,
                  testId: 'dock-next-act-shape',
                },
              ]
            : []
        }
      />
    ),
    [back, kind, t],
  );
}
